import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search, 
  Eye, 
  Calendar, 
  User, 
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  FlaskConical,
  Trash2,
  FileText,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/App";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status color mapping
const SAMPLE_STATUS_COLORS = {
  "Pass": "bg-green-100 text-green-800 border-green-200",
  "Partially Pass": "bg-amber-100 text-amber-800 border-amber-200",
  "Failed": "bg-red-100 text-red-800 border-red-200",
  "Requires Modification": "bg-orange-100 text-orange-800 border-orange-200",
  "Under Observation": "bg-blue-100 text-blue-800 border-blue-200",
  "Completed": "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const DEV_STATUS_COLORS = {
  "New": "bg-slate-100 text-slate-800 border-slate-200",
  "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
  "Under Trial": "bg-purple-100 text-purple-800 border-purple-200",
  "Completed": "bg-green-100 text-green-800 border-green-200",
  "Closed": "bg-gray-100 text-gray-800 border-gray-200",
};

// Iteration options T1-T30
const ITERATION_OPTIONS = Array.from({ length: 30 }, (_, i) => `T${i + 1}`);

// Sample Status options
const SAMPLE_STATUS_OPTIONS = [
  "Pass",
  "Partially Pass", 
  "Failed",
  "Requires Modification",
  "Under Observation",
  "Completed"
];

// Development Status options
const DEV_STATUS_OPTIONS = [
  "New",
  "In Progress",
  "Under Trial",
  "Completed",
  "Closed"
];

// Glass card component
const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-white/90 backdrop-blur-xl border border-white/50 rounded-2xl shadow-xl ${className}`}>
    {children}
  </div>
);

export default function SampleDevelopmentReportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [isFiltered, setIsFiltered] = useState(false);
  
  // Form state
  const [showFormModal, setShowFormModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [labEmployees, setLabEmployees] = useState([]);
  const [productList, setProductList] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
  // Form fields
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formPreparedBy, setFormPreparedBy] = useState("");
  const [formFinalRemarks, setFormFinalRemarks] = useState("");
  const [formProductDetails, setFormProductDetails] = useState([{
    productName: "",
    productNameId: "",
    noOfIterations: [],
    sampleStatus: "",
    finalTrial: "",
    developmentStatus: "",
    developmentActivity: "",
    remarks: ""
  }]);
  
  const recordsPerPage = 15;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/sample-development`, {
        params: {
          user_role: user?.role || '',
          user_name: user?.name || ''
        }
      });
      if (response.data.success) {
        setRecords(response.data.records || []);
        setIsFiltered(response.data.filtered || false);
      } else {
        toast.error(response.data.error || "Failed to load records");
      }
    } catch (error) {
      console.error("Failed to fetch sample development records:", error);
      toast.error("Failed to load sample development records");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchRecords();
    }
  }, [fetchRecords, user]);

  // Fetch dropdown data for form
  const fetchDropdownData = useCallback(async () => {
    setLoadingDropdowns(true);
    try {
      const [labResponse, productResponse] = await Promise.all([
        axios.get(`${API_URL}/api/zoho/lab-employees`),
        axios.get(`${API_URL}/api/zoho/product-list`)
      ]);
      
      if (labResponse.data.success) {
        setLabEmployees(labResponse.data.employees || []);
      }
      if (productResponse.data.success) {
        setProductList(productResponse.data.products || []);
      }
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error);
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  const handleRefresh = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/sample-development/refresh`, null, {
        params: {
          user_role: user?.role || '',
          user_name: user?.name || ''
        }
      });
      if (response.data.success) {
        setRecords(response.data.records || []);
        setIsFiltered(response.data.filtered || false);
        const message = response.data.filtered 
          ? `Showing ${response.data.count} of ${response.data.totalCount} records (your records only)`
          : `Refreshed ${response.data.count} records from Zoho`;
        toast.success(message);
      }
    } catch (error) {
      toast.error("Failed to refresh records");
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetails = async (record) => {
    // Fetch full record with subform data from Zoho
    try {
      const response = await axios.get(`${API_URL}/api/sample-development/${record.zohoId}`);
      if (response.data.success) {
        setSelectedRecord(response.data.record);
      } else {
        setSelectedRecord(record); // Fallback to list data
      }
    } catch (error) {
      console.error("Failed to fetch record details:", error);
      setSelectedRecord(record); // Fallback to list data
    }
    setShowDetailModal(true);
  };

  const handleDeleteClick = (record) => {
    setDeletingRecord(record);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRecord) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/sample-development/${deletingRecord.zohoId}`,
        { params: { user_role: user?.role } }
      );
      
      if (response.data.success) {
        toast.success("Record deleted successfully");
        setRecords(records.filter(r => r.zohoId !== deletingRecord.zohoId));
        setShowDeleteConfirm(false);
        setDeletingRecord(null);
      } else {
        toast.error(response.data.error || "Failed to delete record");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete record";
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (zohoId) => {
    setExpandedRows(prev => ({
      ...prev,
      [zohoId]: !prev[zohoId]
    }));
  };

  // Form handlers
  const handleOpenForm = () => {
    fetchDropdownData();
    resetForm();
    setShowFormModal(true);
  };

  const resetForm = () => {
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormPreparedBy("");
    setFormFinalRemarks("");
    setFormProductDetails([{
      productName: "",
      productNameId: "",
      noOfIterations: [],
      sampleStatus: "",
      finalTrial: "",
      developmentStatus: "",
      developmentActivity: "",
      remarks: ""
    }]);
  };

  const addProductDetailRow = () => {
    setFormProductDetails([...formProductDetails, {
      productName: "",
      productNameId: "",
      noOfIterations: [],
      sampleStatus: "",
      finalTrial: "",
      developmentStatus: "",
      developmentActivity: "",
      remarks: ""
    }]);
  };

  const removeProductDetailRow = (index) => {
    if (formProductDetails.length > 1) {
      setFormProductDetails(formProductDetails.filter((_, i) => i !== index));
    }
  };

  const updateProductDetail = (index, field, value) => {
    const updated = [...formProductDetails];
    updated[index] = { ...updated[index], [field]: value };
    setFormProductDetails(updated);
  };

  // Update multiple fields at once to avoid state race conditions
  const updateProductDetailMultiple = (index, updates) => {
    const updated = [...formProductDetails];
    updated[index] = { ...updated[index], ...updates };
    setFormProductDetails(updated);
  };

  const handleIterationToggle = (index, iteration) => {
    const updated = [...formProductDetails];
    const currentIterations = updated[index].noOfIterations || [];
    if (currentIterations.includes(iteration)) {
      updated[index].noOfIterations = currentIterations.filter(i => i !== iteration);
    } else {
      updated[index].noOfIterations = [...currentIterations, iteration].sort((a, b) => {
        return parseInt(a.slice(1)) - parseInt(b.slice(1));
      });
    }
    setFormProductDetails(updated);
  };

  const handleSubmitForm = async () => {
    if (!formPreparedBy) {
      toast.error("Please select Prepared By");
      return;
    }

    // Validate that at least one product is selected
    const hasValidProduct = formProductDetails.some(pd => pd.productNameId && pd.productNameId.trim() !== '');
    if (!hasValidProduct) {
      toast.error("Please select at least one Product Name");
      return;
    }

    setSubmitting(true);
    try {
      // Format date as DD-MMM-YYYY
      const dateObj = new Date(formDate);
      const formattedDate = dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).replace(/ /g, '-');

      // Filter out rows without product selection
      const validProductDetails = formProductDetails.filter(pd => pd.productNameId && pd.productNameId.trim() !== '');

      const payload = {
        date: formattedDate,
        preparedById: formPreparedBy,
        productDetails: validProductDetails.map(pd => ({
          productName: pd.productName,
          productNameId: pd.productNameId,
          noOfIterations: pd.noOfIterations || [],
          sampleStatus: pd.sampleStatus || '',
          finalTrial: pd.finalTrial || '',
          developmentStatus: pd.developmentStatus || '',
          developmentActivity: pd.developmentActivity || '',
          remarks: pd.remarks || ''
        })),
        finalRemarks: formFinalRemarks
      };

      console.log("Submitting payload:", JSON.stringify(payload, null, 2));

      const response = await axios.post(
        `${API_URL}/api/sample-development/submit`,
        payload,
        { params: { userId: user?.id, userName: user?.name } }
      );

      if (response.data.success) {
        toast.success(`Sample Development ${response.data.sampleDevelopmentNo || ''} submitted successfully!`);
        setShowFormModal(false);
        resetForm();
        // Refresh to show the new record
        await handleRefresh();
      } else {
        toast.error(response.data.error || "Failed to submit");
        console.error("Submission error:", response.data);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || "Failed to submit form";
      toast.error(errorMsg);
      console.error("Submission exception:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter records
  const filteredRecords = records.filter(record => {
    const matchesSearch = 
      record.sampleNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.sampleDevelopmentNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.preparedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.finalRemarks?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen p-6" data-testid="sample-development-page">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-vcc-teal to-vcc-teal/80 rounded-xl shadow-lg">
              <FlaskConical className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-poppins font-bold text-slate-800">
                Sample Development Report
              </h1>
              <p className="text-slate-500 text-sm font-poppins">
                Track development progress from Zoho
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={handleOpenForm}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-poppins"
              data-testid="new-sample-dev-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Sample Development
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={syncing}
              variant="outline"
              className="font-poppins"
              data-testid="refresh-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync from Zoho
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <GlassCard className="p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by Sample Dev No, Prepared By, or Remarks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 font-poppins"
              data-testid="search-input"
            />
          </div>
          
          {isFiltered && !isAdmin && (
            <Badge variant="secondary" className="font-poppins bg-amber-100 text-amber-800 border-amber-200">
              <User className="w-3 h-3 mr-1" />
              My Records Only
            </Badge>
          )}
          
          {isAdmin && (
            <Badge variant="secondary" className="font-poppins bg-blue-100 text-blue-800 border-blue-200">
              All Records
            </Badge>
          )}
          
          <Badge variant="outline" className="font-poppins">
            {filteredRecords.length} records
          </Badge>
        </div>
      </GlassCard>

      {/* Table */}
      <GlassCard className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-vcc-teal" />
            <span className="ml-3 text-slate-600 font-poppins">Loading records...</span>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FlaskConical className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 font-poppins">No sample development records found</p>
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="mt-4 font-poppins"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync from Zoho
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="font-poppins font-semibold">Sample Dev No</TableHead>
                  <TableHead className="font-poppins font-semibold">Date</TableHead>
                  <TableHead className="font-poppins font-semibold">Prepared By</TableHead>
                  <TableHead className="font-poppins font-semibold">Products</TableHead>
                  <TableHead className="font-poppins font-semibold">Final Remarks</TableHead>
                  <TableHead className="font-poppins font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRecords.map((record, index) => (
                  <>
                    <motion.tr
                      key={record.zohoId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      <TableCell>
                        {record.productDetails && record.productDetails.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRowExpansion(record.zohoId)}
                            className="p-1 h-6 w-6"
                          >
                            {expandedRows[record.zohoId] ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="font-poppins font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-vcc-teal" />
                          {record.sampleDevelopmentNo || record.sampleNo || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="font-poppins text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(record.date)}
                        </div>
                      </TableCell>
                      <TableCell className="font-poppins">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {record.preparedBy || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="font-poppins">
                        {record.productDetails && record.productDetails.length > 0 ? (
                          <Badge variant="outline" className="bg-blue-50">
                            <Package className="w-3 h-3 mr-1" />
                            {record.productDetails.length} product(s)
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="font-poppins text-slate-600 max-w-xs truncate">
                        {record.finalRemarks || record.remarks || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(record)}
                            className="text-vcc-teal hover:text-vcc-teal/80 hover:bg-vcc-teal/10"
                            data-testid={`view-record-${record.zohoId}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(record)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-record-${record.zohoId}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </motion.tr>
                    
                    {/* Expanded Product Details */}
                    <AnimatePresence>
                      {expandedRows[record.zohoId] && record.productDetails && record.productDetails.length > 0 && (
                        <motion.tr
                          key={`${record.zohoId}-expanded`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <TableCell colSpan={7} className="bg-slate-50/50 p-0">
                            <div className="p-4">
                              <h4 className="text-sm font-semibold text-slate-700 mb-3 font-poppins flex items-center gap-2">
                                <Package className="w-4 h-4 text-vcc-teal" />
                                Product Details
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-slate-100 border-b">
                                      <th className="text-left p-2 font-poppins font-medium">Product Name</th>
                                      <th className="text-left p-2 font-poppins font-medium">Iterations</th>
                                      <th className="text-left p-2 font-poppins font-medium">Sample Status</th>
                                      <th className="text-left p-2 font-poppins font-medium">Final Trial</th>
                                      <th className="text-left p-2 font-poppins font-medium">Dev Status</th>
                                      <th className="text-left p-2 font-poppins font-medium">Remarks</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {record.productDetails.map((pd, idx) => (
                                      <tr key={idx} className="border-b border-slate-100">
                                        <td className="p-2 font-poppins">{pd.productName || "-"}</td>
                                        <td className="p-2 font-poppins text-xs">
                                          {pd.noOfIterations || "-"}
                                        </td>
                                        <td className="p-2">
                                          {pd.sampleStatus && (
                                            <Badge className={`text-xs ${SAMPLE_STATUS_COLORS[pd.sampleStatus] || "bg-slate-100"}`}>
                                              {pd.sampleStatus}
                                            </Badge>
                                          )}
                                        </td>
                                        <td className="p-2 font-poppins">{pd.finalTrial || "-"}</td>
                                        <td className="p-2">
                                          {pd.developmentStatus && (
                                            <Badge className={`text-xs ${DEV_STATUS_COLORS[pd.developmentStatus] || "bg-slate-100"}`}>
                                              {pd.developmentStatus}
                                            </Badge>
                                          )}
                                        </td>
                                        <td className="p-2 font-poppins text-xs text-slate-600">{pd.remarks || "-"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </TableCell>
                        </motion.tr>
                      )}
                    </AnimatePresence>
                  </>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                <p className="text-sm text-slate-500 font-poppins">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, filteredRecords.length)} of {filteredRecords.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-poppins px-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </GlassCard>

      {/* New Sample Development Form Modal */}
      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className="max-w-6xl w-[95vw] bg-white flex flex-col h-[90vh]">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2 font-poppins text-slate-800">
              <FlaskConical className="w-5 h-5 text-vcc-teal" />
              New Sample Development
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2 space-y-6 mt-4">
            {/* Date and Prepared By */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-poppins">Date</Label>
                <Input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="font-poppins"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-poppins">Prepared By *</Label>
                <Select value={formPreparedBy} onValueChange={setFormPreparedBy}>
                  <SelectTrigger className="font-poppins">
                    <SelectValue placeholder={loadingDropdowns ? "Loading..." : "Select employee"} />
                  </SelectTrigger>
                  <SelectContent>
                    {labEmployees.map((emp) => (
                      <SelectItem key={emp.id || emp.ID} value={emp.id || emp.ID}>
                        {emp.name || emp.Employee_Name || emp.Name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Product Details Subform */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="font-poppins text-base font-semibold">Product Details</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProductDetailRow}
                  className="font-poppins"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Row
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b">
                        <th className="text-left p-3 font-poppins font-medium min-w-[180px]">Product Name</th>
                        <th className="text-left p-3 font-poppins font-medium min-w-[200px]">No of Iterations</th>
                        <th className="text-left p-3 font-poppins font-medium min-w-[150px]">Sample Status</th>
                        <th className="text-left p-3 font-poppins font-medium min-w-[120px]">Final Trial</th>
                        <th className="text-left p-3 font-poppins font-medium min-w-[150px]">Dev Status</th>
                        <th className="text-left p-3 font-poppins font-medium min-w-[150px]">Remarks</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formProductDetails.map((row, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="p-2">
                            <Select 
                              value={row.productNameId || ""} 
                              onValueChange={(val) => {
                                const product = productList.find(p => p.id === val);
                                updateProductDetailMultiple(idx, {
                                  productNameId: val,
                                  productName: product?.productName || ''
                                });
                              }}
                            >
                              <SelectTrigger className="font-poppins text-xs">
                                <SelectValue placeholder="Select Product">
                                  {row.productName || "Select Product"}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {productList.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.productName}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                              {ITERATION_OPTIONS.slice(0, 10).map((iter) => (
                                <label 
                                  key={iter} 
                                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs cursor-pointer border ${
                                    row.noOfIterations?.includes(iter) 
                                      ? 'bg-vcc-teal/20 border-vcc-teal text-vcc-teal' 
                                      : 'bg-slate-50 border-slate-200 text-slate-600'
                                  }`}
                                >
                                  <Checkbox
                                    checked={row.noOfIterations?.includes(iter)}
                                    onCheckedChange={() => handleIterationToggle(idx, iter)}
                                    className="w-3 h-3"
                                  />
                                  {iter}
                                </label>
                              ))}
                            </div>
                          </td>
                          <td className="p-2">
                            <Select 
                              value={row.sampleStatus} 
                              onValueChange={(val) => updateProductDetail(idx, 'sampleStatus', val)}
                            >
                              <SelectTrigger className="font-poppins text-xs">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {SAMPLE_STATUS_OPTIONS.map((status) => (
                                  <SelectItem key={status} value={status}>{status}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Select 
                              value={row.finalTrial} 
                              onValueChange={(val) => updateProductDetail(idx, 'finalTrial', val)}
                            >
                              <SelectTrigger className="font-poppins text-xs">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {ITERATION_OPTIONS.map((iter) => (
                                  <SelectItem key={iter} value={iter}>{iter}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Select 
                              value={row.developmentStatus} 
                              onValueChange={(val) => updateProductDetail(idx, 'developmentStatus', val)}
                            >
                              <SelectTrigger className="font-poppins text-xs">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {DEV_STATUS_OPTIONS.map((status) => (
                                  <SelectItem key={status} value={status}>{status}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Input
                              value={row.remarks}
                              onChange={(e) => updateProductDetail(idx, 'remarks', e.target.value)}
                              placeholder="Remarks"
                              className="text-xs"
                            />
                          </td>
                          <td className="p-2">
                            {formProductDetails.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProductDetailRow(idx)}
                                className="p-1 h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Final Remarks */}
            <div className="space-y-2">
              <Label className="font-poppins">Final Remarks</Label>
              <Textarea
                value={formFinalRemarks}
                onChange={(e) => setFormFinalRemarks(e.target.value)}
                placeholder="Enter final remarks..."
                className="font-poppins min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 mt-4 pt-4 border-t bg-white">
            <Button
              variant="outline"
              onClick={() => setShowFormModal(false)}
              disabled={submitting}
              className="font-poppins"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitForm}
              disabled={submitting}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-poppins"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit to Zoho"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-poppins text-slate-800">
              <FlaskConical className="w-5 h-5 text-vcc-teal" />
              Sample Development Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6 mt-4">
              {/* Sample Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <label className="text-xs text-slate-500 font-poppins uppercase tracking-wide">Sample Development No</label>
                  <p className="text-lg font-semibold text-slate-800 font-poppins mt-1">
                    {selectedRecord.sampleDevelopmentNo || selectedRecord.sampleNo || "-"}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <label className="text-xs text-slate-500 font-poppins uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Date
                  </label>
                  <p className="text-lg font-semibold text-slate-800 font-poppins mt-1">
                    {formatDate(selectedRecord.date)}
                  </p>
                </div>
              </div>

              {/* Prepared By */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <label className="text-xs text-slate-500 font-poppins uppercase tracking-wide flex items-center gap-1">
                  <User className="w-3 h-3" /> Prepared By
                </label>
                <p className="text-lg font-medium text-slate-800 font-poppins mt-1">
                  {selectedRecord.preparedBy || "-"}
                </p>
              </div>

              {/* Product Details */}
              {selectedRecord.productDetails && selectedRecord.productDetails.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700 font-poppins flex items-center gap-2">
                    <Package className="w-4 h-4 text-vcc-teal" />
                    Product Details ({selectedRecord.productDetails.length})
                  </h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100 border-b">
                          <th className="text-left p-3 font-poppins font-medium">Product</th>
                          <th className="text-left p-3 font-poppins font-medium">Iterations</th>
                          <th className="text-left p-3 font-poppins font-medium">Sample Status</th>
                          <th className="text-left p-3 font-poppins font-medium">Final Trial</th>
                          <th className="text-left p-3 font-poppins font-medium">Dev Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRecord.productDetails.map((pd, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-3 font-poppins font-medium">{pd.productName || "-"}</td>
                            <td className="p-3 font-poppins text-xs">{pd.noOfIterations || "-"}</td>
                            <td className="p-3">
                              {pd.sampleStatus && (
                                <Badge className={`text-xs ${SAMPLE_STATUS_COLORS[pd.sampleStatus] || "bg-slate-100"}`}>
                                  {pd.sampleStatus}
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 font-poppins">{pd.finalTrial || "-"}</td>
                            <td className="p-3">
                              {pd.developmentStatus && (
                                <Badge className={`text-xs ${DEV_STATUS_COLORS[pd.developmentStatus] || "bg-slate-100"}`}>
                                  {pd.developmentStatus}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Final Remarks */}
              {(selectedRecord.finalRemarks || selectedRecord.remarks) && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="text-xs text-blue-700 font-poppins uppercase tracking-wide">Final Remarks</label>
                  <p className="text-sm text-blue-800 font-poppins mt-2 whitespace-pre-wrap">
                    {selectedRecord.finalRemarks || selectedRecord.remarks}
                  </p>
                </div>
              )}

              {/* Zoho ID for reference */}
              <div className="p-3 bg-slate-100 rounded-lg">
                <label className="text-xs text-slate-400 font-poppins">Zoho Record ID</label>
                <p className="text-xs font-mono text-slate-500 mt-1">{selectedRecord.zohoId}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-poppins text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Sample Development Record?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              Are you sure you want to delete the record for <strong>{deletingRecord?.sampleDevelopmentNo || deletingRecord?.sampleNo}</strong>? 
              This action cannot be undone and will remove the record from Zoho.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 font-poppins"
              disabled={deleting}
            >
              {deleting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete Record"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
